import { useState } from 'react';
import { cx } from '../cx';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

export type AvatarProps = {
  src?: string | null;
  /** The person's name: the image's alt text and the source of the initials. */
  name: string;
  size?: AvatarSize;
  className?: string;
};

const SIZE: Record<AvatarSize, string> = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-16 type-title',
  xl: 'size-24 type-figure-lg',
};

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** A round photo of a person, or their initials on the neutral elevated fill when there is none or it fails to load. */
export function Avatar({ src, name, size = 'md', className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const base = cx(
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
    SIZE[size],
    className,
  );
  if (src && !failed) {
    return (
      <span className={base}>
        <img src={src} alt={name} className="size-full object-cover" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <span
      role="img"
      aria-label={name}
      className={cx(base, 'bg-jc-elevated font-display font-bold text-jc-ink-secondary')}
    >
      <span aria-hidden="true">{initials(name)}</span>
    </span>
  );
}
